import { useEffect } from "react";

import { Grid, Stack, Typography, Button, Box, Tooltip, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CreateOutlined, AddBoxOutlined, DeleteOutlined } from "@mui/icons-material";

import nameof from "ts-nameof.macro";
import { useFieldArray } from "react-hook-form";
import { useConfirm } from "material-ui-confirm";

import { PublicRoutesConfig } from "../../RoutesConfig";
import LinearStepper from "../../../common/components/LinearStepper";
import Success from "../../shared/SuccessModal";
import AlertExtended from "../../../common/components/mui-extended/AlertExtended";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import DropzoneExtended from "../../../common/components/mui-extended/DropzoneExtended";
import RadioGroupExtended from "../../../common/components/mui-extended/RadioGroupExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { ErrorMessages, Rules } from "../../../common/validationRules";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { Mode, Step, useInputForm } from "../../../common/hooks/useInputForm";
import useUserContext from "../../../hooks/useUserContext";
import fileService from "../../../common/services/fileService";
import notaryService from "../../../services/notaryService";
import settlementService from "../../../services/settlementService";
import withdrawnAuthorizationService from "../../../services/withdrawnAuthorizationService";
import authorizationService from "../../../services/authorizationService";
import { Issuer, WithdrawnAuthorizationInputDto } from "../../../interfaces/WithdrawnAuthorization";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { useNavigate } from "react-router-dom";

export default function Input() {
  const navigate = useNavigate();
  const loading = useAxiosLoader(false);
  const confirm = useConfirm();

  const { userId, isOwner } = useUserContext();

  const defaultModel: WithdrawnAuthorizationInputDto = { userId };

  const form = useInputForm<WithdrawnAuthorizationInputDto>({
    defaultModel: defaultModel,
    addAsync: withdrawnAuthorizationService.addWithdrawnAuthorizationAsync,
    updateAsync: withdrawnAuthorizationService.updateWithdrawnAuthorizationAsync,
    validate: async (model: WithdrawnAuthorizationInputDto) => {
      if (model.issueDate && model.registrationNumber) {
        await withdrawnAuthorizationService
          .getWithdrawnAuthorizationIdByYearAsync(userId, model.registrationNumber, model.issueDate)
          .then(({ data: withdrawnAuthorizationId }) => {
            if (withdrawnAuthorizationId && withdrawnAuthorizationId !== form.id) {
              form.setError(nameof(defaultModel.registrationNumber), ErrorMessages.AlreadyExists);
            }
          });
      }

      for (let i = 0; i < (model?.authorizations?.length || 0); i++) {
        let authorization = model?.authorizations?.[i];

        let notaryId: number | undefined;
        let settlementId: number | undefined;

        if (authorization?.issuer === Issuer.Notary) {
          notaryId = authorization?.notary?.id;
        } else if (authorization?.issuer === Issuer.Settlement) {
          settlementId = authorization?.settlement?.id;
        }

        const registrationNumber = authorization?.registrationNumber;
        const issueDate = authorization?.issueDate;

        if ((notaryId || settlementId) && registrationNumber && issueDate) {
          await authorizationService
            .getAuthorizationIdAsync(registrationNumber, issueDate, notaryId, settlementId)
            .then(({ data: authorizationId }) => {
              if (!!!authorizationId) {
                if (authorization?.issuer === Issuer.Notary) {
                  form.setError(
                    `${nameof(authorizations)}.${i}.${nameof(authorization.notary)}.id`,
                    ErrorMessages.AuthorizationNotFound
                  );
                } else if (authorization?.issuer === Issuer.Settlement) {
                  form.setError(
                    `${nameof(authorizations)}.${i}.${nameof(authorization.settlement)}.id`,
                    ErrorMessages.AuthorizationNotFound
                  );
                }
              }

              if (authorization && authorizationId) {
                form.setValue(
                  `${nameof(authorizations)}.${i}.${nameof(authorization.authorizationId)}`,
                  authorizationId
                );
              }
            });
        }
      }
    },
  });

  const {
    fields: authorizations,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: "authorizations",
  });

  useEffect(() => {
    (async () => {
      if (form.mode === Mode.EDIT) {
        await withdrawnAuthorizationService
          .getWithdrawnAuthorizationAsync(form.id)
          .then(({ data: withdrawnAuthorization }) => {
            const model: WithdrawnAuthorizationInputDto = {
              ...withdrawnAuthorization,
              userId: userId,
              withdrawnAuthorizationId: form.id,
              fileId: withdrawnAuthorization.file?.fileId,
            };

            model.authorizations?.forEach((authorization) => {
              if (authorization.notary) {
                authorization.issuer = Issuer.Notary;
              } else if (authorization.settlement) {
                authorization.issuer = Issuer.Settlement;
              }
            });

            if (isOwner(withdrawnAuthorization.ownerUserIds)) {
              form.recreateForm(model);
            } else {
              navigate(PublicRoutesConfig.Home.Path);
            }
          });
      }
    })();
  }, []);

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderSuccess = () => (
    <Success routePath={PublicRoutesConfig.WithdrawnAuthorization}>
      {`на оттеглено пълномощно ${form.getValues(nameof(defaultModel.registrationNumber))} / ${dateToStringShort(
        form.getValues(nameof(defaultModel.issueDate))
      )}`}
    </Success>
  );

  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack spacing={3}>
        <SectionExtended title="Данни за документ">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Регистрационен номер"
                name={nameof(defaultModel.registrationNumber)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{
                  ...Rules.Required,
                  ...Rules.Number,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата"
                name={nameof(defaultModel.issueDate)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{
                  ...Rules.Required,
                  ...Rules.Date,
                }}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended title="Упълномощител/и">
          <TextFieldExtended
            name={nameof(defaultModel.authorizer)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Упълномощен/и">
          <TextFieldExtended
            name={nameof(defaultModel.authorized)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Данни за документа, с който се оттегля пълномощното">
          <TextFieldExtended
            name={nameof(defaultModel.description)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Данни за оттегляно/ни пълномощно/ни">
          <Stack spacing={3}>
            <AlertExtended type="warning">
              При наличието на упоменат общ регистрационен номер на оттегленото пълномощно в декларацията за оттегляне,
              попълването на тази секция е задължително!{" "}
            </AlertExtended>
            {authorizations.length > 0 && (
              <Stack spacing={3}>
                {authorizations.map((authorization, index) => (
                  <Stack key={authorization.id} spacing={1}>
                    <RadioGroupExtended
                      row
                      name={`${nameof(authorizations)}.${index}.${nameof(authorization.issuer)}`}
                      options={[
                        ["Нотариус", Issuer.Notary],
                        ["Населено място", Issuer.Settlement],
                      ]}
                      onChange={(event: any, value: string) => {
                        if (value === Issuer.Notary) {
                          form.setValue(`${nameof(authorizations)}.${index}.${nameof(authorization.settlement)}`, null);
                          form.unregister(`${nameof(authorizations)}.${index}.${nameof(authorization.settlement)}.id`);
                        } else if (value === Issuer.Settlement) {
                          form.setValue(`${nameof(authorizations)}.${index}.${nameof(authorization.notary)}`, null);
                          form.unregister(`${nameof(authorizations)}.${index}.${nameof(authorization.notary)}.id`);
                        }
                      }}
                      form={form}
                      disabled={form.step === Step.PREVIEW}
                    />
                    <Stack direction="row" spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={5}>
                          <Stack>
                            {form.watch(`${nameof(authorizations)}.${index}.${nameof(authorization.issuer)}`) ===
                              Issuer.Notary && (
                              <SelectExtended
                                label="Нотариус"
                                name={`${nameof(authorizations)}.${index}.${nameof(authorization.notary)}.id`}
                                form={form}
                                searchRequest={notaryService.getNotaries}
                                initRequest={notaryService.getNotariesByIds}
                                disabled={form.step === Step.PREVIEW}
                                rules={Rules.Required}
                              />
                            )}

                            {form.watch(`${nameof(authorizations)}.${index}.${nameof(authorization.issuer)}`) ===
                              Issuer.Settlement && (
                              <SelectExtended
                                label="Населено място"
                                name={`${nameof(authorizations)}.${index}.${nameof(authorization.settlement)}.id`}
                                form={form}
                                searchRequest={settlementService.getSettlements}
                                initRequest={settlementService.getSettlementsByIds}
                                disabled={form.step === Step.PREVIEW}
                                rules={Rules.Required}
                              />
                            )}
                          </Stack>
                        </Grid>
                        <Grid item xs={3}>
                          <TextFieldExtended
                            label="Регистрационен номер"
                            name={`${nameof(authorizations)}.${index}.${nameof(authorization.registrationNumber)}`}
                            form={form}
                            disabled={form.step === Step.PREVIEW}
                            rules={{
                              ...Rules.Required,
                              ...Rules.Number,
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <DatePickerExtended
                            label="Дата"
                            name={`${nameof(authorizations)}.${index}.${nameof(authorization.issueDate)}`}
                            form={form}
                            disabled={form.step === Step.PREVIEW}
                            rules={{
                              ...Rules.Required,
                              ...Rules.Date,
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Box>
                        <Tooltip title="Изтриване">
                          <IconButton
                            disabled={form.step === Step.PREVIEW}
                            onClick={() =>
                              confirm({ title: "Сигурни ли сте?" })
                                .then(() => remove(index))
                                .catch(() => {})
                            }
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
            {form.step === Step.INPUT && (
              <Stack direction="row">
                <Button startIcon={<AddBoxOutlined />} onClick={() => append({})}>
                  Добави
                </Button>
              </Stack>
            )}
          </Stack>
        </SectionExtended>
        <SectionExtended
          title="Сканирано копие (незадължително)"
          defaultExpanded={!!form.watch(nameof(defaultModel.fileId))}
        >
          <DropzoneExtended
            name={nameof(defaultModel.fileId)}
            form={form}
            disabled={form.step === Step.PREVIEW}
            initRequest={fileService.getFileMetadataById}
            uploadRequest={fileService.upload}
          />
        </SectionExtended>
        {form.mode === Mode.EDIT && (
          <SectionExtended title="Забележка (причини за извършване на корекцията)">
            <TextFieldExtended
              name={nameof(defaultModel.correctionReason)}
              multiline
              form={form}
              disabled={form.step === Step.PREVIEW}
              rules={Rules.Required}
            />
          </SectionExtended>
        )}
        <Stack direction="row-reverse" justifyContent="space-between">
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3} minHeight={550}>
      <Typography variant="h5">Регистър оттеглени пълномощни</Typography>
      {renderStepper()}
      {form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
